import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { HouseholdsService } from '../../services/households/households.service';
import { CreateHouseholdRequest } from '../../models/households.model';
import { ToasterService } from '../../services/toaster/toaster.service';
import { UsersService } from '../../services/users/users.service';

@Component({
  selector: 'app-new-household',
  templateUrl: './new-household.component.html',
  styleUrls: ['./new-household.component.scss']
})
export class NewHouseholdComponent implements OnInit {
  @Input() isOpen: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() householdCreated = new EventEmitter<{ id: number }>();

  householdData: CreateHouseholdRequest = {
    HOUSEHOLD_NAME: '',
    ORGANIZATION_ID: undefined
  };

  isSubmitting: boolean = false;
  currentOrganizationId: number | undefined;

  constructor(
    private householdsService: HouseholdsService,
    private usersService: UsersService,
    private toaster: ToasterService
  ) { }

  ngOnInit(): void {
    this.getCurrentUserOrganization();
  }

  /**
   * Get the current user's organization ID
   */
  private async getCurrentUserOrganization(): Promise<void> {
    try {
      const userResponse = await this.usersService.getCurrentUser();
      this.currentOrganizationId = userResponse.ORGANIZATION.ORGANIZATION_ID;
      this.householdData.ORGANIZATION_ID = this.currentOrganizationId;
    } catch (error: any) {
      console.error('Error fetching current user organization:', error);
      // Organization ID will be undefined, which is handled in the form validation
    }
  }

  /**
   * Close the modal
   */
  public closeModal(): void {
    this.close.emit();
    this.resetForm();
  }

  /**
   * Reset the form to its initial state
   */
  private resetForm(): void {
    this.householdData = {
      HOUSEHOLD_NAME: '',
      ORGANIZATION_ID: this.currentOrganizationId
    };
    this.isSubmitting = false;
  }

  /**
   * Submit the form to create a new household
   */
  public async submitForm(): Promise<void> {
    // Validate form
    if (!this.householdData.HOUSEHOLD_NAME) {
      this.toaster.warning('Please enter a household name', 3000);
      return;
    }

    if (!this.householdData.ORGANIZATION_ID) {
      this.toaster.warning('Organization ID is required', 3000);
      return;
    }

    this.isSubmitting = true;

    try {
      const response = await this.householdsService.createHousehold(this.householdData);
      this.toaster.success('Household created successfully', 3000);

      // Extract HOUSEHOLD_ID from response (handles Snowflake PK gotcha where it might be undefined)
      const householdId = response?.HOUSEHOLD_ID ?? 0;
      if (!householdId) {
        console.warn('Household created but HOUSEHOLD_ID was not returned in response:', response);
      }

      this.householdCreated.emit({ id: householdId });
      this.resetForm();
    } catch (error: any) {
      this.toaster.warning(`Error creating household: ${error?.message ?? error}`, 4000);
      console.error('Error creating household:', error);
    } finally {
      this.isSubmitting = false;
    }
  }
}
